// What the tree's ticks mean, as pure rules the page and its tests share.
//
// A tick is remembered as a NODE KEY (category:…, family:…|…, type:…|…|…,
// element:…) so the scope can be re-resolved against a later publish
// rather than frozen as a list of ids. Two things make ticks fragile
// with a tree that loads one level at a time, and both are handled here
// rather than in the component: the antd tree only "conducts" a tick
// down to children it has loaded, and it only reports keys it knows.

// The tree hands back a ticked section AND everything loaded under it.
// The scope remembers the section: "Cable Trays" means the cable trays
// in the model today, and a saved list of its 412 types would stop
// meaning that at the next publish.
export function minimalKeys(keys) {
  const set = new Set((keys || []).map(String));
  const covered = (k) => {
    const at = k.indexOf(":");
    const kind = k.slice(0, at);
    const parts = k.slice(at + 1).split("|");
    if (kind === "family") return set.has(`category:${parts[0]}`);
    if (kind === "type") return set.has(`category:${parts[0]}`) || set.has(`family:${parts[0]}|${parts[1]}`);
    return false;
  };
  return [...set].filter((k) => !covered(k));
}

export function parentKey(key) {
  const k = String(key || "");
  const at = k.indexOf(":");
  const kind = k.slice(0, at);
  const parts = k.slice(at + 1).split("|");
  if (kind === "family") return `category:${parts[0]}`;
  if (kind === "type") return `family:${parts[0]}|${parts[1]}`;
  return null;
}

// The tree reports only the keys of nodes it has loaded. A saved pick
// deeper than what is open - a type under a category nobody expanded
// this session, or an element picked in the model - would be dropped
// from the report the moment anything else is ticked, and the save that
// follows would silently forget it. So the keys the tree does not know
// are carried over from the previous state, unless the section above
// them was just unticked, which is the one case where forgetting them is
// what the person meant.
export function mergeCheckedKeys(prevChecked, nextChecked, loadedKeys) {
  const loaded = new Set([...(loadedKeys || [])].map(String));
  const prev = (prevChecked || []).map(String);
  const next = new Set((nextChecked || []).map(String));
  const prevSet = new Set(prev);
  const untickedSections = new Set(prev.filter((k) => loaded.has(k) && !next.has(k) && !k.startsWith("element:")));
  const underUnticked = (k) => {
    let p = parentKey(k);
    while (p) {
      if (untickedSections.has(p)) return true;
      p = parentKey(p);
    }
    return false;
  };
  for (const k of prev) {
    if (loaded.has(k) || next.has(k)) continue;
    if (underUnticked(k)) continue;
    next.add(k);
  }
  // Nothing the tree just reported can be under a section it also just
  // unticked, so no second pass; prevSet is only here to keep the input
  // order for keys that survived.
  const ordered = prev.filter((k) => next.has(k));
  for (const k of next) if (!prevSet.has(k)) ordered.push(k);
  return ordered;
}

// The keys that would put ONE element in the scope, from the record the
// board has for it: the element itself, its type, its family, its
// category. Callers pick the level they mean.
export function keysForElement(el) {
  if (!el) return {};
  const c = el.category || "";
  const f = el.family || "";
  const t = el.typeName || "";
  return {
    element: el.uniqueId ? `element:${el.uniqueId}` : null,
    type: t ? `type:${c}|${f}|${t}` : null,
    family: f ? `family:${c}|${f}` : null,
    category: c ? `category:${c}` : null,
  };
}

// The model view's selection: a plain click picks one thing, a
// CTRL-click adds or removes. NOT shift: the viewer reads e.ctrlKey and
// nothing else (webgl/js/viewer/index.js), so a shift-click has always
// behaved as a plain click. Said here because three comments in this page
// claimed shift worked, and a comment that promises a modifier nobody
// reads is how a "bug" gets reported that was never built.
// a CTRL-click adds or removes it, so a run of fittings can be
// gathered and marked in one go.
// A CLICK ON NOTHING CLEARS EVERYTHING, CTRL OR NOT (owner, 2026-09-30:
// "if the viewer does report empty space click/0 elements, we need to make
// everything else deselect for ease of selection navigation"). It used to
// return the list unchanged on a ctrl-click, which is why ctrl-clicking empty
// space appeared to do nothing - there was nothing wrong with the click, the
// answer was "no change". Clearing on a miss is what every 3D tool does and it
// is the cheap way back to an empty selection without hunting for the button.
export function nextSelection(prev, uid, additive) {
  const list = (prev || []).map(String);
  if (!uid) return [];
  if (!additive) return [String(uid)];
  const i = list.indexOf(String(uid));
  if (i >= 0) { const out = list.slice(); out.splice(i, 1); return out; }
  return [...list, String(uid)];
}
